import { useState } from "react";
import { UserServices } from "$services";
import { User } from "$types/User";
import { ROLES } from "$services/Coordinators.service";

import {
  useUsersQuery,
  useAddUserMutation,
  useEditUserMutation,
} from "$hooks/useUsersQuery";

const useUserForm = (companyId?: string, warehouse?: string) => {
  const { data: users = [], isLoading: usersLoader } = useUsersQuery();
  const addUserMutation = useAddUserMutation();
  const editUserMutation = useEditUserMutation();

  const [user, setUser] = useState<User>(null);
  const [open, setOpen] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [title, setTitle] = useState<string>("");

  const togglePopin = (isOpen: boolean) => {
    if (!isOpen) {
      setUser(null);
      setError("");
    }
    setOpen(isOpen);
  };

  const onAddClick = () => {
    setUser(null);
    togglePopin(true);
  };

  const onEditClick = (_user: User) => {
    if (_user) {
      setUser(_user);
      togglePopin(true);
    }
  };

  const onSubmit = async (user: User) => {
    const {
      activity,
      email,
      firstname,
      isOnlyContact,
      isApprover,
      job,
      lastname,
      phone,
      phone2,
    } = user;
    if (lastname && firstname && email) {
      try {
        const data = await addUserMutation.mutateAsync({
          user: {
            activity,
            company: companyId,
            email,
            firstname,
            isOnlyContact,
            isApprover,
            job,
            lastname,
            phone,
            phone2,
            role: companyId ? ROLES.CLIENT : ROLES.ADMIN,
            warehouse,
          },
        });

        if (data?.error) {
          setError(data.error);
        } else {
          togglePopin(false);
          setError("");
        }
        return data;
      } catch (e: any) {
        setError(e.message || "Une erreur est survenue");
        return { error: e.message };
      }
    }
    setError(UserServices.ERRORS.MISSING_FIELD);
    return null;
  };

  const onSubmitContact = async (user: User) => {
    return onSubmit({ ...user, isOnlyContact: true });
  };

  const onSubmitEdit = async (user: User) => {
    const {
      activity,
      email,
      firstname,
      isOnlyContact,
      isApprover,
      job,
      lastname,
      phone,
      phone2,
      auth0Id,
    } = user;

    if (firstname !== "" && lastname !== "" && email !== "") {
      try {
        const res = await editUserMutation.mutateAsync({
          data: {
            user: {
              activity,
              company: companyId,
              email,
              firstname,
              isOnlyContact,
              isApprover,
              job,
              lastname,
              phone,
              phone2,
              auth0Id,
              role: isOnlyContact
                ? null
                : companyId
                  ? ROLES.CLIENT
                  : ROLES.ADMIN,
            },
          },
          userId: user?._id || "",
        });

        if (res) {
          togglePopin(false);
          setError("");
          setUser(null);
        } else {
          setError("Une erreur s'est produite lors de l'édition utilisateur");
        }
        return res;
      } catch (e: any) {
        setError(e.message || "Une erreur est survenue");
        return null; // Or handle error appropriately
      }
    }
    return null;
  };

  return {
    onAddClick,
    onEditClick,
    users,
    user,
    setUser,
    open,
    togglePopin,
    onSubmit,
    onSubmitContact,
    onSubmitEdit,
    error,
    title,
    setTitle,
    loading:
      usersLoader || addUserMutation.isPending || editUserMutation.isPending,
    setError,
  };
};

export default useUserForm;
